import { Reveal } from "./Reveal";
import LangLink from "./LangLink";
import { Info, Calendar, UserCheck, PenLine, ArrowUpRight, ExternalLink, TableProperties } from "lucide-react";

// Summary / "at a glance" box shown near the top of long pages.
export const SummaryBox = ({ children, title = "In short" }) => (
  <Reveal className="bg-surface border-l-2 border-accent rounded-r-lg p-6 md:p-7" data-testid="summary-box">
    <div className="flex items-center gap-2 mb-3">
      <Info className="w-4 h-4 text-accent" strokeWidth={1.75} />
      <span className="text-xs uppercase tracking-[0.16em] font-semibold text-accent font-sans">{title}</span>
    </div>
    <p className="text-[16px] text-secondary-foreground leading-relaxed">{children}</p>
  </Reveal>
);

// On-page table of contents built from section {id, h2}.
export const TableOfContents = ({ sections = [] }) => (
  <nav aria-label="On this page" className="bg-background border border-border rounded-lg p-6" data-testid="table-of-contents">
    <h2 className="text-xs uppercase tracking-[0.16em] font-semibold text-muted-foreground font-sans mb-4">On this page</h2>
    <ol className="space-y-2.5">
      {sections.map((s, i) => (
        <li key={s.id}>
          <a href={`#${s.id}`} className="group flex items-baseline gap-2.5 text-[14px] text-primary/80 hover:text-accent transition-colors font-sans">
            <span className="text-accent tabular-nums text-xs">{String(i + 1).padStart(2, "0")}</span>
            <span className="group-hover:underline underline-offset-4">{s.h2}</span>
          </a>
        </li>
      ))}
    </ol>
  </nav>
);

// Editorial trust metadata (E-E-A-T). Reviewer is optional; when absent we
// honestly show "Medical review pending" rather than inventing a reviewer.
export const ArticleMeta = ({ author = "Plastic Surgery Vietnam Editorial Team", reviewer, published, updated }) => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-muted-foreground font-sans border-y border-border py-4" data-testid="article-meta">
    <span className="flex items-center gap-2"><PenLine className="w-4 h-4 text-accent" strokeWidth={1.5} /> Written by {author}</span>
    <span className="flex items-center gap-2">
      <UserCheck className="w-4 h-4 text-accent" strokeWidth={1.5} />
      {reviewer ? `Reviewed by ${reviewer}` : "Medical review pending"}
    </span>
    {published && <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-accent" strokeWidth={1.5} /> Published {published}</span>}
    {updated && <span className="flex items-center gap-2">Last reviewed {updated}</span>}
  </div>
);

// Price-table placeholder — structure ready, populated later with verified ranges.
export const PriceTablePlaceholder = ({ rows = [] }) => (
  <div className="border border-border rounded-lg overflow-hidden" data-testid="price-table-placeholder">
    <div className="flex items-center gap-2 bg-surface px-5 py-3 border-b border-border">
      <TableProperties className="w-4 h-4 text-accent" strokeWidth={1.5} />
      <span className="text-xs uppercase tracking-[0.14em] font-semibold text-primary font-sans">Indicative cost factors</span>
    </div>
    <table className="w-full text-[14px] font-sans">
      <thead>
        <tr className="text-left text-muted-foreground border-b border-border">
          <th className="px-5 py-3 font-medium">Cost component</th>
          <th className="px-5 py-3 font-medium">Indicative range</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r} className="border-b border-border/60">
            <td className="px-5 py-3 text-secondary-foreground">{r}</td>
            <td className="px-5 py-3 text-muted-foreground italic">Provided after assessment</td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="px-5 py-3 text-xs text-muted-foreground bg-surface/50">Verified ranges are provided in an indicative quotation after review. We do not publish fixed prices.</p>
  </div>
);

// "Sources and further reading" — curated authoritative links only.
export const SourcesList = ({ sources = [] }) => {
  if (!sources.length) return null;
  return (
    <div className="mt-12 border-t border-border pt-8" data-testid="sources-list">
      <h2 className="font-serif text-2xl text-primary mb-4">Sources & further reading</h2>
      <ul className="space-y-2.5">
        {sources.map((s) => (
          <li key={s.url}>
            <a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="group inline-flex items-start gap-2 text-[15px] text-primary/85 hover:text-accent transition-colors">
              <ExternalLink className="w-4 h-4 text-accent shrink-0 mt-1" strokeWidth={1.5} />
              <span className="underline underline-offset-4 decoration-border group-hover:decoration-accent">{s.label}</span>
            </a>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-muted-foreground">External links are provided for general reference and do not imply endorsement.</p>
    </div>
  );
};

// "Related guides / procedures" link cluster with descriptive anchors.
export const RelatedLinks = ({ title = "Related guides", links = [] }) => {
  if (!links.length) return null;
  return (
    <div className="mt-12 border-t border-border pt-8" data-testid="related-links">
      <h2 className="font-serif text-2xl text-primary mb-5">{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {links.map((l) => (
          <LangLink key={l.to} to={l.to} className="group flex items-start justify-between gap-3 bg-surface border border-border rounded-lg p-5 hover:border-accent transition-colors" data-testid={`related-${l.to.replace(/\//g, "")}`}>
            <span>
              <span className="block font-sans font-medium text-[15px] text-primary group-hover:text-accent transition-colors">{l.title}</span>
              {l.desc && <span className="block text-[13px] text-muted-foreground mt-0.5">{l.desc}</span>}
            </span>
            <ArrowUpRight className="w-4 h-4 text-accent opacity-60 group-hover:opacity-100 shrink-0 mt-0.5" />
          </LangLink>
        ))}
      </div>
    </div>
  );
};
